
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { Field, Form, Formik } from 'formik'
import { NextSeo } from 'next-seo'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import phone from 'phone'
import { useMemo, useState, useSyncExternalStore } from 'react'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'
import { customerState } from '@/app/auth/auth.state'
import { useMutation, useQuery } from '@/app/common/graphql/hooks'
import { Button } from '@/components/Button'
import Input from '@/components/Input'
import CarFormProgress from '@/domains/car/components/CarForm/CarFormProgress'
import CarFormStep from '@/domains/car/components/CarForm/CarFormStep'
import {
  MIN_SELECTABLE_REGISTRATION_YEAR,
  RegistrationYear,
  useParseRegistrationYear,
  useRedirectOnParseError,
} from '@/domains/car/helpers/parseCarFormQuery'
import {
  PageProps,
  createGetServerSideProps,
} from '@/helpers/createGetServerSideProps'
import * as tracking from '@/helpers/tracking'
import useCompany from '@/helpers/useCompany'
import { useDebouncedCallback } from '@/helpers/useDebouncedCallback'
import { EVehicleCategory } from '@/types'
import {
  CreateCustomDealDocument,
  CreateCustomDealMutation,
  CreateCustomDealMutationVariables,
  EDealType,
  ERegionCode,
  GetItemQuestionsByCategoryDocument,
  GetItemQuestionsByCategoryQuery,
  GetItemQuestionsByCategoryQueryVariables,
  ItemAnswerArgs,
  ItemQuestion,
} from '@/types/gql/graphql'

type ContactDataPageProps = PageProps<{}>

export default function ContactDataPage(props: ContactDataPageProps) {
  const { t } = useTranslation('carPawnFormContactDataStep')
  const ContactDataSchema = useContactDataSchema(props.regionCode)
  const [persistedData, setPersistedData] = usePersistedData()

  const registrationYearResult = useParseRegistrationYear()

  useRedirectOnParseError(registrationYearResult)

  const { zulassungsjahr, marke, modell, betrag } =
    registrationYearResult[0] ?? {}

  const handleSubmit = useHandleSubmit({
    ...props,
    make: marke,
    model: modell,
    registrationYear: zulassungsjahr,
    payoutAmount: betrag,
  })

  return (
    <CarFormStep step={4}>
      <NextSeo noindex nofollow title={t('title')} />

      <div>
        <CarFormProgress step={4} />

        <h1 className="text-heading-m text-primary text-center my-2">
          {t('contact_data_question')}
        </h1>
      </div>

      <Formik
        initialValues={persistedData}
        enableReinitialize
        validationSchema={ContactDataSchema}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {({ values, touched, errors, submitCount, isSubmitting }) => {
          setPersistedData(values)

          return (
            <Form>
              <Field
                name="name"
                label={t('name')}
                as={Input}
                error={(touched.name || !!submitCount) && errors.name}
              />

              <Field
                name="email"
                type="email"
                label={t('email')}
                className="mt-3"
                as={Input}
                error={(touched.email || !!submitCount) && errors.email}
              />

              <Field
                name="phone"
                type="tel"
                label={t('phone')}
                className="mt-3"
                as={Input}
                error={(touched.phone || !!submitCount) && errors.phone}
              />

              <Button
                variant="cta"
                type="submit"
                className="w-full mt-6 h-12"
                loading={isSubmitting}
              >
                {t('request_offer')}
              </Button>
            </Form>
          )
        }}
      </Formik>
    </CarFormStep>
  )
}

interface ContactData {
  name: string
  phone: string
  email: string
}

function usePersistedData() {
  const snapshot = useMemo(() => {
    if (typeof sessionStorage === 'undefined') {
      return defaultInitialData
    }

    const raw = sessionStorage.getItem(storageKey)
    if (raw) {
      try {
        return JSON.parse(raw)
      } catch {
        sessionStorage.deleteItem(storageKey)
      }
    }

    // create a copy so data !== defaultInitialData
    return { ...defaultInitialData }
  }, [])

  const persistedData = useSyncExternalStore(
    subscribe,
    () => snapshot,
    getServerSnapshot,
  )

  const [setPersistedData] = useDebouncedCallback(
    (data: ContactData) =>
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.setItem(storageKey, JSON.stringify(data)),
    500,
  )

  return [persistedData, setPersistedData]
}

function subscribe() {
  return function unsubsribe() {}
}

function getServerSnapshot() {
  return defaultInitialData
}

const defaultInitialData: ContactData = {
  name: '',
  phone: '',
  email: '',
}

const storageKey = 'car-form-contact-data'

 const _getServerSideProps = createGetServerSideProps()

interface HandleSubmitArgs extends ContactDataPageProps {
  payoutAmount?: number
  make?: string
  model?: string
  registrationYear?: RegistrationYear
}

function useHandleSubmit({ regionCode, ...props }: HandleSubmitArgs) {
  const company = useCompany()
  const customer = useRecoilValue(customerState)

  const [createCustomDeal] = useMutation<
    CreateCustomDealMutation,
    CreateCustomDealMutationVariables
  >(CreateCustomDealDocument)

  const continueUsing = company?.headquarter?.countryCode === 'AT'
  const itemCategoryId = continueUsing
    ? EVehicleCategory.CAR
    : EVehicleCategory.CAR_STORED

  const itemQuestionAnswers = useItemQuestionAnswers({
    continueUsing,
    itemCategoryId,
  })

  const router = useRouter()

  return async (values: ContactData) => {
    tracking.user({ email: values.email, phone: values.phone }, regionCode)

    const nameFragments = values.name.split(' ')
    const firstname = nameFragments
      .slice(0, Math.max(nameFragments.length - 1, 1))
      .join(' ')
    const lastname = nameFragments.length > 1 ? nameFragments.at(-1) : undefined

    const { payoutAmount, make, model, registrationYear } = props
    if (!payoutAmount || !make || !model || !registrationYear) {
      throw new Error('Missing required data')
    }

    const registrationYearNote =
      registrationYear === 'before-range'
        ? (`Erstzulassung vor ${MIN_SELECTABLE_REGISTRATION_YEAR}` as const)
        : undefined

    const res = await createCustomDeal({
      variables: {
        customDealCreateArgs: {
          dealType: EDealType.Pawn,
          companyId: company?._id,
          contactData: {
            email: values.email,
            phone: values.phone,
            firstname,
            lastname,
          },
          durationInDays: 30,
          customerId: customer?._id,
          notes: registrationYearNote && [{ content: registrationYearNote }],
          items: [
            {
              note: registrationYearNote,
              title: [
                registrationYear !== 'before-range' && registrationYear,
                make,
                model,
              ]
                .filter((v) => !!v)
                .join(' '),
              answers: itemQuestionAnswers,
              itemCategoryId,
              pawnPayoutAmount: payoutAmount,
              purchasePayoutAmount: payoutAmount,
              vehicleData: {
                vehicleProperties: {
                  make,
                  model,
                  regdate:
                    registrationYear === 'before-range'
                      ? null
                      : registrationYear.toFixed(),
                },
              },
            },
          ],
        },
      },
    })

    if (res.data) {
      try {
        tracking.trackDealBooked({
          dealId: res.data.createCustomDeal._id,
          type: 'pawn',
          items: [
            {
              categoryHierarchy: [itemCategoryId],
            },
          ],
        })
      } finally {
        router.push('/autopfand-formular/anfrage-versendet')
        // wait for navigation
        await new Promise((r) => setTimeout(r, 5_000))
      }
    }
  }
}

function useItemQuestionAnswers({
  itemCategoryId,
  continueUsing,
}: {
  itemCategoryId: string
  continueUsing: boolean
}) {
  const [date] = useState(() => new Date())
  const { data: normalCarQuestionsData } = useQuery<
    GetItemQuestionsByCategoryQuery,
    GetItemQuestionsByCategoryQueryVariables
  >(GetItemQuestionsByCategoryDocument, {
    variables: {
      itemCategoryId,
      date,
      isCustomItem: true,
      omitDeactivatedQuestionsForNewProducts: false,
    },
  })

  const { data: customCarQuestionsData } = useQuery<
    GetItemQuestionsByCategoryQuery,
    GetItemQuestionsByCategoryQueryVariables
  >(GetItemQuestionsByCategoryDocument, {
    variables: {
      itemCategoryId,
      date,
      isCustomItem: false,
      omitDeactivatedQuestionsForNewProducts: false,
    },
  })

  return useMemo(() => {
    const itemQuestions: Omit<ItemQuestion, 'validTo' | 'validFrom'>[] = [
      ...(normalCarQuestionsData?.getItemQuestionsByCategory ?? []),
      ...(customCarQuestionsData?.getItemQuestionsByCategory ?? []),
    ]
    return itemQuestions.map((question): ItemAnswerArgs => {
      return isContinueUsingQuestion(question)
        ? {
            questionId: question._id,
            selectedOptionIndex: continueUsing ? 0 : 1,
          }
        : {
            questionId: question._id,
            selectedOptionIndex: question.singleChoiceOptions?.findIndex(
              (choice) => choice.isDefaultValue,
            ),
          }
    })
  }, [
    normalCarQuestionsData?.getItemQuestionsByCategory,
    customCarQuestionsData?.getItemQuestionsByCategory,
    continueUsing,
  ])
}

function isContinueUsingQuestion(question: Pick<ItemQuestion, 'titleKey'>) {
  return question.titleKey === 'QUESTION_CUSTOMER_CONTINUE_USING_ITEM'
}

function useContactDataSchema(regionCode: ERegionCode) {
  const { t } = useTranslation('carPawnFormContactDataStep')
  return useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(t('validation_errors.missing_name')),
        email: Yup.string()
          .email(t('validation_errors.invalid_email'))
          .required(t('validation_errors.missing_email')),
        phone: Yup.string()
          .test(
            'phone',
            t('validation_errors.invalid_phone'),
            (phoneNumber) => {
              if (typeof phoneNumber !== 'string') {
                return false
              }

              const { isValid } = phone(phoneNumber, {
                country:
                  phoneNumber.startsWith('00') || phoneNumber.startsWith('+')
                    ? undefined
                    : regionCode,
                strictDetection: false,
                validateMobilePrefix: false,
              })

              return isValid
            },
          )
          .required(t('validation_errors.missing_phone')),
      }),
    [regionCode, t],
  )
}


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/autopfand-formular/kontaktdaten',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  